import { Fixture } from '../models/fixture-models';

/**
 * The two tenant algorithms for auto-selecting a fixture when there is no (or no matching)
 * `heimspielId` — see `PredictionGameTenantConfig.initialFixtureRule` for the full doc.
 */
export type InitialFixtureRule = 'next-tippable' | 'played-until-next-day-end';

/**
 * Finds the index of the next tippable fixture:
 * - editable AND not finished (the backend's dev data flags even FINISHED fixtures as
 *   editable, so the flag alone is not trustworthy), preferring the earliest kickoff at/after
 *   `now`. If every tippable kickoff already passed (stale data), the one closest to now
 *   (latest) wins.
 * - If nothing is tippable at all (season over), falls back to the fixture with the latest
 *   kickoff.
 * - Returns `0` for an empty array.
 */
export function findNextTippableFixtureIndex(fixtures: Fixture[], now: Date = new Date()): number {
  if (fixtures.length === 0) return 0;

  let nextUpcoming = -1;
  let latestTippable = -1;
  let latest = 0;

  fixtures.forEach((fixture, index) => {
    const kickoff = new Date(fixture.startingAt);
    if (kickoff > new Date(fixtures[latest].startingAt)) {
      latest = index;
    }

    const tippable = fixture.userPredictionEditable && !fixture.isFinished && fixture.status !== 'FINISHED';
    if (!tippable) return;

    if (kickoff >= now && (nextUpcoming === -1 || kickoff < new Date(fixtures[nextUpcoming].startingAt))) {
      nextUpcoming = index;
    }
    if (latestTippable === -1 || kickoff > new Date(fixtures[latestTippable].startingAt)) {
      latestTippable = index;
    }
  });

  if (nextUpcoming !== -1) return nextUpcoming;
  if (latestTippable !== -1) return latestTippable;
  return latest;
}

/**
 * A played fixture's selection window, based on `graceMinutes`:
 * - `null` (default/production rule): stays "current" until the end of the
 *   calendar day AFTER its kickoff (local time) — e.g. a Saturday 15:30 kickoff
 *   stays selected through Sunday 23:59:59.999, and only from Monday does the
 *   next upcoming fixture take over.
 * - a number: stays "current" for exactly `graceMinutes` minutes after kickoff
 *   (used to speed up manual testing against generated data in dev builds).
 */
function selectionCutoff(startingAt: string, graceMinutes: number | null): Date {
  const kickoff = new Date(startingAt);
  if (graceMinutes !== null) {
    return new Date(kickoff.getTime() + graceMinutes * 60_000);
  }
  return new Date(kickoff.getFullYear(), kickoff.getMonth(), kickoff.getDate() + 1, 23, 59, 59, 999);
}

/**
 * Finds the index of the fixture that should be auto-selected in the match
 * paginator, based on `now`:
 * - Among fixtures still within their selection window (cutoff >= now), picks the
 *   one with the earliest kickoff (the current/next relevant match). The window
 *   is either `graceMinutes` after kickoff, or — when `graceMinutes` is `null`
 *   (the default) — the end of the calendar day after kickoff. See
 *   {@link selectionCutoff}.
 * - If every fixture's window has passed (season over), falls back to the fixture
 *   with the latest kickoff.
 * - Returns `0` for an empty array.
 */
export function findFixtureIndexWithinSelectionWindow(
  fixtures: Fixture[],
  now: Date = new Date(),
  graceMinutes: number | null = null,
): number {
  if (fixtures.length === 0) return 0;

  let currentIndex = -1;
  let latestIndex = 0;

  fixtures.forEach((fixture, index) => {
    if (new Date(fixture.startingAt) > new Date(fixtures[latestIndex].startingAt)) {
      latestIndex = index;
    }

    if (selectionCutoff(fixture.startingAt, graceMinutes) >= now) {
      if (currentIndex === -1 || new Date(fixture.startingAt) < new Date(fixtures[currentIndex].startingAt)) {
        currentIndex = index;
      }
    }
  });

  return currentIndex !== -1 ? currentIndex : latestIndex;
}

export interface FindInitialFixtureIndexOptions {
  /**
   * Id of the fixture the host app wants shown first — the `MATCH_CONTEXT` abstraction's
   * `heimspielId` (query param, provider or internal fixture id). `null` = no request.
   */
  heimspielId: string | null;
  /** Tenant's configured fallback algorithm — see `PredictionGameTenantConfig.initialFixtureRule`. */
  rule: InitialFixtureRule;
  now?: Date;
  /**
   * Only used by the `'played-until-next-day-end'` rule — see
   * `PredictionGameTenantConfig.devSelectionGraceMinutes`. Ignored by `'next-tippable'`.
   */
  graceMinutes?: number | null;
}

/**
 * Finds the index of the fixture that should be auto-selected when the fixtures load:
 * - If `heimspielId` is set and matches a fixture (by provider id, falling back to the
 *   internal id), that fixture ALWAYS wins — regardless of `rule`.
 * - Otherwise — or when `heimspielId` matches nothing — the tenant's `rule` decides:
 *   - `'next-tippable'`: {@link findNextTippableFixtureIndex}.
 *   - `'played-until-next-day-end'`: {@link findFixtureIndexWithinSelectionWindow} (using
 *     `graceMinutes`, when given).
 * - Returns `0` for an empty array.
 */
export function findInitialFixtureIndex(
  fixtures: Fixture[],
  { heimspielId, rule, now = new Date(), graceMinutes = null }: FindInitialFixtureIndexOptions,
): number {
  if (fixtures.length === 0) return 0;

  if (heimspielId) {
    const requested = fixtures.findIndex((fixture) => fixture.providerId === heimspielId || fixture.id === heimspielId);
    if (requested !== -1) return requested;
  }

  switch (rule) {
    case 'played-until-next-day-end':
      return findFixtureIndexWithinSelectionWindow(fixtures, now, graceMinutes);
    case 'next-tippable':
      return findNextTippableFixtureIndex(fixtures, now);
  }
}

/** Es darf immer nur die nächste Partie getippt werden. Ein Spiel ist
 *  „zukünftig" (Countdown + Hinweistext statt Banner, alles gesperrt), wenn es
 *  SCHEDULED und nicht editierbar ist und NACH dem aktuell tippbaren Spiel
 *  anstößt. Anker ist das vom Backend als editierbar markierte Spiel — nicht
 *  das erste nicht-beendete: die Dev-Daten enthalten gesperrte PLANNED-Spiele
 *  VOR dem tippbaren, die schlicht gesperrt sind, nicht „zukünftig". Läuft die
 *  nächste Partie gerade (LIVE, nichts editierbar), ist sie der Anker. */
export function isFutureFixture(fixtures: Fixture[], index: number): boolean {
  const fixture = fixtures[index];
  if (!fixture || fixture.status !== 'SCHEDULED' || fixture.userPredictionEditable) return false;
  const anchor =
    fixtures.find((f) => f.status === 'SCHEDULED' && f.userPredictionEditable && !f.isFinished) ??
    fixtures.find((f) => f.status === 'LIVE');
  return !!anchor && new Date(fixture.startingAt) > new Date(anchor.startingAt);
}
